// Rendering helpers: React -> offscreen DOM -> html2canvas -> PNG/JPEG/PDF
import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { domToCanvas } from 'modern-screenshot';
import jsPDF from 'jspdf';
import { StaticPage } from '../components/editor/ObjectRenderer';
import { fillToCss } from './colors';
import { embeddedFontCss, familiesIn } from './fontEmbed';

const waitForImages = (host, timeout = 8000) => {
  const imgs = Array.from(host.querySelectorAll('img'));
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => { img.onload = res; img.onerror = res; })))),
    new Promise((res) => setTimeout(res, timeout)),
  ]);
};

/** Load every (family, weight, style) combination the elements under `host` use — bounded wait, never throws. */
const loadFontsIn = (host, timeout = 6000) => {
  if (!document.fonts?.load) return Promise.resolve();
  const wanted = new Set();
  for (const el of [host, ...host.querySelectorAll('*')]) {
    if (!(el instanceof Element)) continue;
    const cs = getComputedStyle(el);
    const fam = String(cs.fontFamily || '').split(',')[0].replace(/["']/g, '').trim();
    if (!fam) continue;
    wanted.add(`${cs.fontStyle || 'normal'} ${cs.fontWeight || '400'} 16px "${fam}"`);
  }
  if (!wanted.size) return Promise.resolve();
  const loads = [...wanted].map((spec) => document.fonts.load(spec).catch(() => null));
  return Promise.race([Promise.all(loads), new Promise((res) => setTimeout(res, timeout))]);
};

/**
 * Render a React element offscreen and rasterise it.
 * Primary path: modern-screenshot (the browser itself paints the DOM inside an SVG foreignObject, so CSS filters,
 * masks, 3-D transforms, reflections and web fonts export exactly as seen on the canvas).
 * Fallback: html2canvas, if the browser blocks the foreignObject approach (e.g. a tainted cross-origin image).
 */
export const renderReactToCanvas = async (element, width, height, { scale = 2, transparent = false, background = '#ffffff' } = {}) => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.className = 'wp-export-host'; // screen-only effects (shimmer) are hidden inside export hosts
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${width}px;height:${height}px;overflow:hidden;background:${transparent ? 'transparent' : background};`;
  document.body.appendChild(host);
  const root = createRoot(host);
  root.render(element);
  // Wait until React has actually committed the element into the host (when this is called from inside another root's
  // effect — e.g. the colour sampler — the nested render can land a few frames later than the usual two), then give the
  // browser one more frame for layout.
  await new Promise((res) => {
    let frames = 0;
    const tick = () => {
      if (host.firstElementChild || frames++ > 120) requestAnimationFrame(res);
      else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
  await waitForImages(host);
  // Make sure every web font the page uses is actually loaded (display=swap fonts the screen never showed are still
  // "unloaded" at this point and `fonts.ready` would resolve without them), then wait for the loads to settle.
  await loadFontsIn(host);
  if (document.fonts?.ready) {
    try { await document.fonts.ready; } catch { /* ignore */ }
  }
  // Web fonts travel inside the rendered image as data: URLs (see lib/fontEmbed.js) — Google Fonts loaded on demand and
  // user fonts are otherwise lost and the export falls back to Arial.
  let fontCss = '';
  try { fontCss = await embeddedFontCss(familiesIn(host), host.textContent || ''); } catch { fontCss = ''; }
  if (fontCss) {
    const st = document.createElement('style');
    st.setAttribute('data-wp-embedded-fonts', '1');
    st.textContent = fontCss;
    host.prepend(st);
  }
  // one more frame: fonts applied, tab spans measured (lib/tabLayout.js) before the pixels are read
  await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
  try {
    try {
      const canvas = await domToCanvas(host, {
        scale,
        width,
        height,
        backgroundColor: transparent ? null : background,
        fetch: { requestInit: { mode: 'cors', credentials: 'omit' }, bypassingCache: false },
        features: { removeControlCharacter: false },
        font: fontCss ? { cssText: fontCss } : undefined,
        timeout: 15000,
      });
      // Sanity check: a blank canvas (all transparent) with content present means the foreignObject path failed silently.
      if (canvas && canvas.width > 0 && !isCanvasBlank(canvas, host)) return canvas;
      throw new Error('modern-screenshot produced an empty canvas');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[renderUtils] modern-screenshot failed, falling back to html2canvas:', err?.message || err);
      return await html2canvas(host, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: transparent ? null : background,
        width,
        height,
        logging: false,
      });
    }
  } finally {
    root.unmount();
    host.remove();
  }
};

/** True when the canvas is fully transparent although the host has visible children. */
const isCanvasBlank = (canvas, host) => {
  if (!host.firstElementChild) return false;
  try {
    const ctx = canvas.getContext('2d');
    const step = Math.max(1, Math.floor(Math.min(canvas.width, canvas.height) / 24));
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let y = 0; y < canvas.height; y += step) {
      for (let x = 0; x < canvas.width; x += step) {
        if (data[(y * canvas.width + x) * 4 + 3] !== 0) return false;
      }
    }
    return true;
  } catch {
    return false;
  }
};

/* ------------------------------------------------------------------ */
/* Printer's marks                                                     */
/* ------------------------------------------------------------------ */

/** Bleed (white margin) that surrounds the page when marks are on — 3/8 in by default, like a commercial print PDF. */
export const MARKS_BLEED = 36;
/** Bleed widths print shops ask for: 1/8, 1/4 and 3/8 in (in px at 96 dpi). */
export const BLEED_OPTIONS = [
  { value: 12, label: '⅛ in', hint: '3 mm — most digital print shops' },
  { value: 24, label: '¼ in', hint: '6 mm' },
  { value: 36, label: '⅜ in', hint: '9 mm — commercial print PDFs' },
];
export const normalizeBleed = (b) => (BLEED_OPTIONS.some((o) => o.value === Number(b)) ? Number(b) : MARKS_BLEED);
/** Mark geometry for a bleed width: a gap so marks never touch the trimmed page, then a line that fits inside the bleed. */
const markGeometry = (bleed) => {
  const gap = Math.max(3, Math.min(6, Math.round(bleed / 6)));
  return { gap, len: Math.max(4, bleed - gap - 1) };
};

export const marksEnabled = (marks) => !!(marks && (marks.crop || marks.fold));

/** Size of the rendered sheet: the page itself, or page + bleed on every side when marks are on. */
export const pageRenderSize = (page, marks) => {
  const b = marksEnabled(marks) ? normalizeBleed(marks.bleed) : 0;
  return { width: page.width + b * 2, height: page.height + b * 2, bleed: b };
};

/** Fold guides for the marks: guide objects with kind 'fold' (brochure templates create them; any guide can be marked). */
export const foldGuidesOf = (guides = []) => guides.filter((g) => g && g.kind === 'fold' && g.orientation && Number.isFinite(Number(g.position)));

/**
 * Crop marks (corner trim lines) and fold marks (dashed lines in the bleed, at each fold guide) drawn around a page of
 * `w` × `h` px placed at (bleed, bleed). Rendered at `scale`.
 */
export const PrinterMarks = ({ w, h, bleed, marks, guides = [], scale = 1 }) => {
  if (!marksEnabled(marks)) return null;
  const W = w + bleed * 2, H = h + bleed * 2;
  const { gap: g, len: L } = markGeometry(bleed);
  const stroke = '#000';
  const sw = Math.max(1, 0.75 * scale) / scale; // hairline: 1 px at 96 dpi, 0.75 px at higher print resolutions
  const lines = [];
  if (marks.crop) {
    const corners = [[bleed, bleed, -1, -1], [bleed + w, bleed, 1, -1], [bleed, bleed + h, -1, 1], [bleed + w, bleed + h, 1, 1]];
    corners.forEach(([x, y, dx, dy], i) => {
      lines.push(<line key={`c${i}h`} x1={x + dx * g} y1={y} x2={x + dx * (g + L)} y2={y} stroke={stroke} strokeWidth={sw} />);
      lines.push(<line key={`c${i}v`} x1={x} y1={y + dy * g} x2={x} y2={y + dy * (g + L)} stroke={stroke} strokeWidth={sw} />);
    });
  }
  if (marks.fold) {
    foldGuidesOf(guides).forEach((fg, i) => {
      const pos = Number(fg.position);
      if (fg.orientation === 'vertical') {
        if (pos <= 0 || pos >= w) return;
        const x = bleed + pos;
        lines.push(<line key={`f${i}t`} x1={x} y1={bleed - g} x2={x} y2={bleed - g - L} stroke={stroke} strokeWidth={sw} strokeDasharray={`${3} ${2}`} />);
        lines.push(<line key={`f${i}b`} x1={x} y1={bleed + h + g} x2={x} y2={bleed + h + g + L} stroke={stroke} strokeWidth={sw} strokeDasharray={`${3} ${2}`} />);
      } else {
        if (pos <= 0 || pos >= h) return;
        const y = bleed + pos;
        lines.push(<line key={`f${i}l`} x1={bleed - g} y1={y} x2={bleed - g - L} y2={y} stroke={stroke} strokeWidth={sw} strokeDasharray={`${3} ${2}`} />);
        lines.push(<line key={`f${i}r`} x1={bleed + w + g} y1={y} x2={bleed + w + g + L} y2={y} stroke={stroke} strokeWidth={sw} strokeDasharray={`${3} ${2}`} />);
      }
    });
  }
  return (
    <svg width={W * scale} height={H * scale} viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }} data-testid="printer-marks" data-crop={marks.crop ? 1 : 0} data-fold={marks.fold ? foldGuidesOf(guides).length : 0} data-bleed={bleed} aria-hidden="true">
      {lines}
    </svg>
  );
};

/**
 * A page rendered inside its bleed with the marks around it (what Export / Print rasterise when marks are on).
 * Like a print-ready PDF, the page background and any object that runs past the page edge continue into the bleed
 * (clipped at the bleed edge) so a slightly-off trim never leaves a white sliver; the marks sit on top.
 */
export const MarkedPage = ({ page, master, textFlow = {}, marks, guides = [], scale = 1 }) => {
  const { width, height, bleed } = pageRenderSize(page, marks);
  if (!bleed) return <StaticPage page={page} master={master} scale={scale} textFlow={textFlow} />;
  const bg = page.background || { type: 'solid', color: '#ffffff' };
  return (
    <div style={{ position: 'relative', width: width * scale, height: height * scale, background: fillToCss(bg, '#ffffff'), overflow: 'hidden' }} data-testid="marked-page" data-bleed={bleed}>
      <div style={{ position: 'absolute', left: bleed * scale, top: bleed * scale, width: page.width * scale, height: page.height * scale }}>
        <StaticPage page={page} master={master} scale={scale} textFlow={textFlow} showBackground={false} style={{ overflow: 'visible' }} />
      </div>
      <PrinterMarks w={page.width} h={page.height} bleed={bleed} marks={marks} guides={guides} scale={scale} />
    </div>
  );
};

/**
 * Rasterise a page. `marks` = { crop, fold, guides } adds a white bleed with crop / fold marks around the page
 * (the canvas is then larger than the page — see pageRenderSize).
 */
export const renderPageToCanvas = (page, master, textFlow = {}, scale = 2, marks = null) => {
  if (!marksEnabled(marks)) return renderReactToCanvas(<StaticPage page={page} master={master} scale={1} textFlow={textFlow} />, page.width, page.height, { scale });
  const { width, height } = pageRenderSize(page, marks);
  return renderReactToCanvas(<MarkedPage page={page} master={master} textFlow={textFlow} marks={marks} guides={marks.guides || []} scale={1} />, width, height, { scale });
};

/** Longest side of a publication's list thumbnail (px). ~360 px JPEG keeps each publication under ~40 KB. */
export const PUBLICATION_THUMB_SIZE = 360;

/**
 * Snapshot of page 1 for the Home / Open lists, as a small JPEG data URL — or undefined when rendering fails (the list
 * then renders the page live). Works from the live editor state or a stashed document tab (same shape).
 */
// capturePublicationThumbnail lives in lib/sheetRender.js (it renders the opening spread for booklets).

/** Bounding box of a set of objects (takes rotation into account approximately). */
export const objectsBounds = (objects) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const o of objects) {
    const cx = o.x + o.width / 2, cy = o.y + o.height / 2;
    const rad = ((o.rotation || 0) * Math.PI) / 180;
    const w = Math.abs(o.width * Math.cos(rad)) + Math.abs(o.height * Math.sin(rad));
    const h = Math.abs(o.width * Math.sin(rad)) + Math.abs(o.height * Math.cos(rad));
    minX = Math.min(minX, cx - w / 2); maxX = Math.max(maxX, cx + w / 2);
    minY = Math.min(minY, cy - h / 2); maxY = Math.max(maxY, cy + h / 2);
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 1, height: 1 };
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
};

/** Render only the given objects (e.g. selection) to a canvas, optionally with transparent background. */
export const renderObjectsToCanvas = (objects, { scale = 2, transparent = true, background = '#ffffff', padding = 4, textFlow = {} } = {}) => {
  const b = objectsBounds(objects);
  const shifted = objects.map((o) => ({ ...o, x: o.x - b.x + padding, y: o.y - b.y + padding }));
  const page = { width: Math.ceil(b.width + padding * 2), height: Math.ceil(b.height + padding * 2), background: { type: transparent ? 'none' : 'solid', color: background }, objects: shifted };
  return renderReactToCanvas(<StaticPage page={page} master={null} scale={1} textFlow={textFlow} showBackground={!transparent} />, page.width, page.height, { scale, transparent, background });
};

export const canvasToBlob = (canvas, type = 'image/png', quality = 0.92) => new Promise((res) => canvas.toBlob(res, type, quality));

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const downloadDataUrl = (dataUrl, filename) => {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const PX_TO_MM = 25.4 / 96;

/** Build a multi-page PDF from pages (rendered at `scale`). Returns a jsPDF instance. */
export const pagesToPdf = async (pages, masters = {}, textFlow = {}, { scale = 2, onProgress, marks = null } = {}) => {
  const size = (p) => pageRenderSize(p, marks);
  const first = size(pages[0]);
  const pdf = new jsPDF({ orientation: first.width > first.height ? 'landscape' : 'portrait', unit: 'mm', format: [first.width * PX_TO_MM, first.height * PX_TO_MM] });
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    const sz = size(page);
    onProgress && onProgress(i + 1, pages.length);
    if (i > 0) pdf.addPage([sz.width * PX_TO_MM, sz.height * PX_TO_MM], sz.width > sz.height ? 'landscape' : 'portrait');
    const canvas = await renderPageToCanvas(page, masters[page.masterId || 'default'], textFlow, scale, marks);
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, sz.width * PX_TO_MM, sz.height * PX_TO_MM);
  }
  return pdf;
};

export const safeFileName = (name) => (name || 'publication').replace(/[^a-z0-9-_ .]/gi, '_').trim() || 'publication';
